/**
 * Site-wide SEO constants — the single place the canonical host, the brand
 * entity and the organisation facts are declared.
 *
 * Everything here must be TRUE and VISIBLE somewhere on the public site
 * (footer, contact page, intro pages). Do not add claims, counts, ratings,
 * partners or profiles that cannot be verified first-party.
 *
 * Pure data module (no imports, no JSX) on purpose: the build-time scripts in
 * scripts/seo/ import it with Node directly.
 */

/** Canonical origin. Never a trailing slash. Verified 01-Oct-2026: the apex
 *  host is a registrar forwarder; CloudFront serves www. */
export const SITE_URL = 'https://www.theskillgenie.com';
export const SITE_HOST = 'www.theskillgenie.com';
export const APEX_HOST = 'theskillgenie.com';

export const SITE_NAME = 'The Skill Genie';
export const SITE_ALIASES = ['TSG', 'TheSkillGenie', 'Skill Genie'];
export const SITE_TAGLINE = 'Empowering Future Builders. Engineering Impact.';
export const LOCALE = 'en_IN';

/** One factual description used everywhere the entity is described.
 *  Checked against the live product on 01-Oct-2026: structured technology
 *  tracks, AI tutor, skill tests, real projects on GitHub with AI code review,
 *  ExiTracker engineering workflow, mock coding/technical/HR interviews,
 *  AI resume review, multilingual learning pages. */
export const SITE_DESCRIPTION =
  'The Skill Genie is an AI-powered industry readiness platform from Evolve X Innovations that helps students, graduates and professionals build practical software engineering skills through structured technology learning, real projects on GitHub, AI-assisted explanations and code review, mock interviews, AI resume review and engineering-workflow practice.';

/** Default social image (served from the first-party asset bucket). */
export const DEFAULT_OG_IMAGE =
  'https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png';
export const DEFAULT_OG_IMAGE_ALT = 'The Skill Genie — AI-Powered Industry Readiness Platform';
export const LOGO_URL = DEFAULT_OG_IMAGE;

export const TWITTER_SITE = '@EvolveX2025';

/** Stable JSON-LD identifiers. */
export const IDS = {
  organization: `${SITE_URL}/#organization`,
  parentOrganization: 'https://www.evolvexinnovations.org/#organization',
  website: `${SITE_URL}/#website`,
  nexusProduct: `${SITE_URL}/tsg-product-engg#product`,
};

/** Parent company — facts shown in the footer and contact page. */
export const PARENT_ORG = {
  name: 'Evolve X Innovations',
  // Both spellings are used in first-party material (invoices use "EvolveX").
  alternateName: ['EvolveX Innovations', 'Evolve X Innovations Pvt Ltd'],
  url: 'https://www.evolvexinnovations.org/',
};

export const CONTACT = {
  email: 'connect@evolvexinnovations.org',
  telephone: '+91 62815 97586',
  address: {
    streetAddress: 'Bhoomatha Thalluri Heights, Kankipadu',
    addressLocality: 'Vijayawada',
    addressRegion: 'Andhra Pradesh',
    postalCode: '521151',
    addressCountry: 'IN',
  },
};

/** Official profiles — the same four the footer links to. Add others only
 *  after they are linked from the footer (first-party declaration). */
export const SAME_AS = [
  'https://facebook.com/theskillgenie',
  'https://x.com/EvolveX2025',
  'https://www.instagram.com/theskillgenie_',
  'https://www.youtube.com/@EvolveXInnovations',
];

/** Sister products referenced on the intro pages. */
export const SISTER_PRODUCTS = {
  exihire: { name: 'ExiHire', url: 'https://exihire.com/', description: 'AI-powered hiring platform' },
  exitracker: { name: 'ExiTracker', url: 'https://www.exitracker.com/', description: 'Work-management tool for engineering teams' },
};

/** Robots directives. */
export const ROBOTS_INDEX = 'index, follow, max-image-preview:large';
export const ROBOTS_NOINDEX = 'noindex, nofollow';
